import React, { useEffect, useRef, Suspense, lazy } from 'react';
import { StickyHeader } from '../components/StickyHeader';
import { FloatingCheckoutCTA } from '../components/FloatingCheckoutCTA';
import { useScrollReveal } from '../hooks/useScrollReveal';
// Lazy-load below-the-fold components to shrink the initial JS bundle and improve TBT/LCP.
// These only render after the user has scrolled past the hero + transformation block.
const Countdown = lazy(() => import('../components/Countdown').then(m => ({
  default: m.Countdown
})));
const FAQAccordion = lazy(() => import('../components/FAQAccordion').then(m => ({
  default: m.FAQAccordion
})));
// Tiny placeholder used by all <Suspense> boundaries — keeps layout stable while chunks load.
const LazyFallback = ({
  minH = '24rem'
}: {
  minH?: string;
}) => <div style={{
  minHeight: minH
}} aria-hidden="true" data-id="element-23" />;
import { trackEvent, PRODUCT_VALUE, PRODUCT_CURRENCY, PRODUCT_NAME, PRODUCT_ID } from '../utils/metaPixel';
const heroImg = 'https://cdn.magicpatterns.com/uploads/wW6s9hVDQ2XFmRGN8Pyfyo/COLECCION_COMPLETA.webp';
const videoPromo = 'https://cdn.magicpatterns.com/uploads/9pUU5HeHYT42qcyoZjjfKF/0512.mp4';
const coverMain = 'https://cdn.magicpatterns.com/uploads/356mwdNZ9RrAbj7sE4rxtS/ME_MIRO.webp';
const coverPalabras = 'https://cdn.magicpatterns.com/uploads/bCAcfQj75WVKBZa13WRwuk/LAS_PALABRAS.webp';
const coverValiente = 'https://cdn.magicpatterns.com/uploads/kWZwZePEBmGxii67vkW29K/VALIENTE.webp';
const coverCalma = 'https://cdn.magicpatterns.com/uploads/3eV5FQLcTmoXgoKaa3Pk3p/CALMA.webp';
const coverEspejo = 'https://cdn.magicpatterns.com/uploads/1WR5Nm7rFSe56qmYEeftSC/ME_MIRO.webp';
const coverTalComo = 'https://cdn.magicpatterns.com/uploads/vGzetQo8MahiaBRL7QFEeX/TAL_COMO_SOY.webp';
const coverCuido = 'https://cdn.magicpatterns.com/uploads/iCf7CgWehnHw3Ata6AwgVe/ME_CUIDO.webp';
const coverDecidir = 'https://cdn.magicpatterns.com/uploads/1YewnjiNitfCsB5okGBBwH/DECIDIR.webp';
const coverPuedo = 'https://cdn.magicpatterns.com/uploads/9XjHVNLmGJ7pTz7RdJcgPs/SI_QUIERO,_PUEDO.webp';
const coverEquivoco = 'https://cdn.magicpatterns.com/uploads/oMHbRy6vJxqUfiH6acYafT/EQUIVOCARSE.webp';
const coverCuadernillo = 'https://cdn.magicpatterns.com/uploads/dMosoyzKtxCWC3Q7JSnCXd/CUADERNILLO_DE_ACTIVIDADES.webp';
const imgCancionesMindset = 'https://cdn.magicpatterns.com/uploads/d4SyPtKN7vF5DHrhPp7k5w/ChatGPT_Image_9_may_2026,_03_49_35_p.m..png';
const imgCuadernilloCover = 'https://cdn.magicpatterns.com/uploads/dMosoyzKtxCWC3Q7JSnCXd/CUADERNILLO_DE_ACTIVIDADES.webp';
const imgGuiaPadres = 'https://cdn.magicpatterns.com/uploads/aaxi6vUyc8LCpGLUjJ8gRM/PORTADAGUIAPARAPADRES.png';
const imgColeccionCuentos = 'https://cdn.magicpatterns.com/uploads/dK6LQJ1VnNb7Afsz8yAN45/PORTADA_SUPERPODERES.png';
// Imágenes editoriales (debajo del fold, lazy-loaded)
const imgFrasesDuelen = 'https://cdn.magicpatterns.com/uploads/wcjwt1fAUAk4RG1G4frE6S/ERNESTOSOLO.webp';
const imgBerrinches = 'https://cdn.magicpatterns.com/uploads/rW8V5dhkas7NyG1zgcg8WG/NECESITAAYUDA.webp';
const imgCulpaSilenciosa = 'https://cdn.magicpatterns.com/uploads/2fhE6rQX2Skochtmy7G4Tk/Gemini_Generated_Image_r3klotr3klotr3kl.png';
const imgCierreEmocional = 'https://cdn.magicpatterns.com/uploads/feV1Bjxk5daaAxVrC6okuT/ChatGPT_Image_14_may_2026,_07_19_23_a.m..png';
// Pares antes/después
const imgDespuesCordones = 'https://cdn.magicpatterns.com/uploads/856Y9zT6quh1RgGQtykGVP/PAPA_AYUDANDO.webp';
const imgDespuesEspejo = 'https://cdn.magicpatterns.com/uploads/rDYYc59nkHwLfmRfkF9hVD/Gemini_Generated_Image_7bcwqw7bcwqw7bcw.png';
const imgDespuesParque = 'https://cdn.magicpatterns.com/uploads/eak6meAua7RPsB1xXkU3df/ERNESTOESSUFICIENTE.webp';
const CHECKOUT_URL = 'https://tienda.sonriealmitamia.com/cart/44371041878058:1';
const ctaPrimary = 'inline-flex items-center justify-center gap-2 bg-gold text-foreground font-display font-extrabold rounded-full px-8 py-4 text-lg shadow-gold hover:scale-[1.03] hover:brightness-105 transition-all duration-300';
const ctaCoral = 'inline-flex items-center justify-center gap-2 bg-coral text-white font-display font-bold rounded-full px-6 py-3 shadow-coral hover:scale-[1.03] transition-all';
const superpowers = [{
  n: 1,
  bg: 'bg-pastel-yellow',
  cover: coverTalComo,
  title: 'Autoestima',
  desc: 'Aprende a reconocer su valor propio sin compararse con los demás.'
}, {
  n: 2,
  bg: 'bg-pastel-mint',
  cover: coverPalabras,
  title: 'Palabras que fortalecen',
  desc: 'Transforma su diálogo interno: del "no puedo" al "yo puedo, yo voy a lograrlo".'
}, {
  n: 3,
  bg: 'bg-pastel-lilac',
  cover: coverEspejo,
  title: 'Amor Propio',
  desc: 'Se mira al espejo y se acepta con amor, exactamente como es.'
}, {
  n: 4,
  bg: 'bg-pastel-blue',
  cover: coverValiente,
  title: 'Valentía',
  desc: 'Entiende que ser valiente no es no tener miedo, sino avanzar aunque el corazón le palpite rápido.'
}, {
  n: 5,
  bg: 'bg-pastel-peach',
  cover: coverCalma,
  title: 'Autorregulación',
  desc: 'Aprende técnicas simples de respiración para volver a la calma antes de desbordarse.'
}, {
  n: 6,
  bg: 'bg-pastel-pink',
  cover: coverEquivoco,
  title: 'Resiliencia',
  desc: 'Descubre que equivocarse no es fracasar, sino el camino exacto para aprender.'
}, {
  n: 7,
  bg: 'bg-pastel-aqua',
  cover: coverDecidir,
  title: 'Autonomía',
  desc: 'Gana confianza para tomar sus propias decisiones y sentirse capaz.'
}, {
  n: 8,
  bg: 'bg-pastel-lavender',
  cover: coverCuido,
  title: 'Responsabilidad',
  desc: 'Aprende el autocuidado y la importancia de cuidar sus cosas.'
}, {
  n: 9,
  bg: 'bg-pastel-orange',
  cover: coverPuedo,
  title: 'Confianza en sí mismo',
  desc: 'Interioriza que puede lograr lo que se propone cuando confía en su propio poder interior.'
}];
const deliverables = [{
  icon: '📘',
  title: 'Colección completa de cuentos «Los superpoderes que hay en mí»',
  desc: 'Todos los cuentos en formato PDF de alta calidad, listos para leer en cualquier dispositivo o imprimir en casa. La historia de Ernesto y todos los superpoderes emocionales que explorará junto a él.'
}, {
  icon: '📱',
  title: 'Acceso a la aplicación con videos tutoriales guiados',
  desc: 'Ingresa a nuestra plataforma complementaria con videos cortos que te guían paso a paso en cómo acompañar a tu hijo en cada superpoder. Perfecto para padres sin tiempo.'
}, {
  icon: '✂️',
  title: 'Cuaderno de recortes especial',
  desc: 'Material adicional para potenciar la coordinación visomotora y la motricidad fina de tu pequeño mientras se divierte.'
}, {
  icon: '📓',
  title: 'Cuadernillo de actividades de desarrollo personal infantil',
  desc: 'Un cuadernillo lúdico con ejercicios, dinámicas y actividades guiadas para que tu hijo entrene cada superpoder mientras se divierte. Pensado para reforzar lo aprendido en los cuentos.'
}, {
  icon: '📖',
  title: 'Guía para padres «Cría niños conscientes»',
  desc: 'Una guía exclusiva para acompañar a tu hijo paso a paso mientras lee los cuentos. Reflexiones, preguntas y actividades para conectar, entender y transformar su mundo emocional junto a vos.'
}, {
  icon: '⚡',
  title: 'Acceso inmediato garantizado',
  desc: 'En menos de 5 minutos desde tu compra, recibirás todo el material en la bandeja de entrada de tu correo electrónico.'
}];
const testimonials = [{
  initials: 'M.G.',
  color: 'bg-pastel-peach',
  name: 'Marcela G.',
  child: 'Mamá de Sofía (4 años)',
  title: '«Lo que meses de intentos no logré, lo logró un cuento»',
  text: "Nunca pensé que un cuento pudiera lograr lo que meses de intentos míos no habían conseguido. Mi hija ahora cuando se frustra se pone la mano en el pecho y dice 'calma, calma'. Lo aprendió sola del cuento. Lloro cada vez que lo recuerdo."
}, {
  initials: 'R.M.',
  color: 'bg-pastel-mint',
  name: 'Roberto M.',
  child: 'Papá de Mateo (6 años)',
  title: '«En 2 semanas dejó de decir "soy el más torpe"»',
  text: "Mi hijo decía 'soy el más torpe de la clase' cada vez que algo le salía mal. Empezamos a leer la colección juntos y en 2 semanas el cambio fue brutal. Ahora dice 'todavía no me sale, pero lo voy a intentar otra vez'. No tiene precio."
}, {
  initials: 'C.L.',
  color: 'bg-pastel-yellow',
  name: 'Carla L.',
  child: 'Abuela de Juan (5 años)',
  title: '«Nos sana a las tres generaciones»',
  text: 'Se lo regalé a mi hija para su hijo y las dos terminamos llorando leyendo los cuentos. Es exactamente lo que necesitábamos nosotras también. Le explica al niño lo que a nosotras nos costó 30 años entender.'
}];
const faqs = [{
  q: '¿En qué formato recibiré el material?',
  a: 'Recibirás todos los archivos en formato PDF de alta resolución directamente en la bandeja de entrada de tu correo electrónico después de tu compra. Podrás leerlos en tu teléfono, tablet, computadora o imprimirlos en casa.'
}, {
  q: '¿Para qué edades están diseñados los cuentos?',
  a: 'La colección está diseñada específicamente para niños de 3 a 10 años, una etapa crítica del desarrollo neurológico y emocional donde estos aprendizajes generan el mayor impacto a largo plazo.'
}, {
  q: '¿Necesito conocimientos de psicología para usar este material?',
  a: 'Para nada. La colección está diseñada para que cualquier padre, madre, abuelo o cuidador pueda leerla con su hijo sin ningún conocimiento previo.'
}, {
  q: '¿Funciona para niños con necesidades especiales o muy sensibles?',
  a: 'Los recursos y técnicas de la colección (respiración, anclajes somáticos, manejo del diálogo interno) son especialmente efectivos para niños altamente sensibles o con alta reactividad emocional. Sin embargo, si tu hijo tiene un diagnóstico clínico específico, te recomendamos complementarlo siempre con el apoyo de un profesional de la salud.'
}, {
  q: '¿Qué pasa si no estoy satisfecho con el material?',
  a: 'Queremos que tengas una experiencia 100% positiva. Si por alguna razón el material no cumple con tus expectativas, contáctanos y encontraremos la mejor solución para ti.'
}, {
  q: '¿Este material sirve para usar en el aula?',
  a: '¡Absolutamente! Muchas maestras y maestros de preescolar y primaria han utilizado estos recursos en el aula con resultados sorprendentes. La licencia de uso es personal y familiar.'
}];
const Avatars = () => <div className="flex -space-x-3" data-id="element-24">
    {['bg-pastel-peach', 'bg-pastel-mint', 'bg-pastel-yellow'].map((c, i) => <div key={i} className={`w-10 h-10 rounded-full border-2 border-card ${c} flex items-center justify-center font-display font-bold text-foreground/80 text-sm`} data-id="element-25">
        {['M', 'R', 'C'][i]}
      </div>)}
  </div>;
const PaymentTrust = ({
  className = ''
}: {
  className?: string;
}) => <div className={`flex flex-col items-center gap-2.5 ${className}`} data-id="element-26">
    <p className="text-sm font-display font-semibold text-foreground/75" data-id="element-27">
      Pagá de forma segura
    </p>
    <div className="flex items-center gap-5 flex-wrap justify-center" data-id="element-28">
      {/* Visa */}
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 780 500" className="h-6 w-auto" aria-label="Visa" role="img" data-id="element-29">
        <path fill="#1A1F71" d="M293.6 334.1h-48.5l30.3-187.1h48.5zm176.2-181.8c-9.6-3.6-24.7-7.5-43.6-7.5-48 0-81.8 24.2-82.1 58.8-.3 25.6 24.2 39.9 42.7 48.4 19 8.7 25.4 14.3 25.3 22.1-.1 11.9-15 17.4-28.8 17.4-19.2 0-29.5-2.7-45.3-9.3l-6.2-2.8-6.8 42c11.2 4.9 31.9 9.2 53.4 9.4 51 0 84.1-23.8 84.5-60.7.2-20.3-12.8-35.7-40.8-48.4-17-8.2-27.5-13.7-27.4-22 .1-7.4 8.8-15.3 27.9-15.3 15.9-.3 27.4 3.2 36.4 6.8l4.4 2 6.4-40.9zm125.4 181.8h42.4l-37-187.1h-39.1c-8.8 0-16.2 4.8-19.5 12.2l-72.4 174.9h51l10.1-27.9h62.3zm-38.2-64.5 25.6-70.2 14.7 70.2zm-335.6-122.6-47.5 127.5-5.1-25.1c-8.8-28.1-36.2-58.5-66.9-73.7l43.5 158.8h51.4l76.5-187.5z" data-id="element-30" />
      </svg>
      {/* Mastercard */}
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" className="h-7 w-auto" aria-label="Mastercard" role="img" data-id="element-31">
        <circle cx="18" cy="24" r="10" fill="#EB001B" data-id="element-32" />
        <circle cx="30" cy="24" r="10" fill="#F79E1B" data-id="element-33" />
        <path fill="#FF5F00" d="M24 14a10 10 0 0 0 0 20 10 10 0 0 0 0-20z" data-id="element-34" />
      </svg>
      {/* Mercado Pago */}
      <img src="https://cdn.magicpatterns.com/uploads/qSCjqbwtUx44s8TKzp13Jv/image.png" alt="Mercado Pago" loading="lazy" decoding="async" className="h-7 w-auto object-contain" data-id="element-35" />
    </div>
  </div>;
const Index = () => {
  useScrollReveal();
  useEffect(() => {
    document.title = 'Los Superpoderes que hay en mí | Colección de Cuentos para la Inteligencia Emocional Infantil';
  }, []);
  // ===== Meta Pixel event handlers =====
  const handleLead = (ctaLabel: string) => {
    trackEvent('Lead', {
      content_name: PRODUCT_NAME,
      content_category: 'cta-click',
      source: ctaLabel
    });
  };
  const handleViewContent = (ctaLabel: string) => {
    trackEvent('ViewContent', {
      content_name: PRODUCT_NAME,
      content_ids: [PRODUCT_ID],
      content_type: 'product',
      source: ctaLabel
    });
  };
  const handleInitiateCheckout = (ctaLabel: string) => {
    trackEvent('InitiateCheckout', {
      content_name: PRODUCT_NAME,
      content_ids: [PRODUCT_ID],
      content_type: 'product',
      value: PRODUCT_VALUE,
      currency: PRODUCT_CURRENCY,
      num_items: 1,
      source: ctaLabel
    });
  };
  // Auto-fire ViewContent when the offer section is at least 50% visible
  const ofertaRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const el = ofertaRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    let fired = false;
    const obs = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!fired && entry.isIntersecting) {
          fired = true;
          handleViewContent('offer-section-visible');
          obs.disconnect();
        }
      });
    }, {
      threshold: 0.5
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return <div id="top" className="min-h-screen bg-background font-sans" data-id="element-36">
      <StickyHeader data-id="element-37" />
      <FloatingCheckoutCTA href={CHECKOUT_URL} onClick={() => handleInitiateCheckout('floating-sticky-cta')} data-id="element-38" />
      {/* ========== BLOQUE 2: HERO ========== */}
      <section className="bg-gradient-hero" data-id="element-39">
        <div className="container mx-auto px-4 md:px-6 pt-4 md:pt-6 pb-6 md:pb-10 flex flex-col gap-6 items-stretch max-w-4xl" data-id="element-40">
          <div className="reveal" data-id="element-41">
            <h1 className="sr-only" data-id="element-42">
              Descubre los superpoderes que hay en ti — Colección de cuentos
              para desarrollar la inteligencia emocional infantil
            </h1>
            {/* Promesa emocional (encima del poster) */}
            <p className="text-center text-xl sm:text-2xl md:text-3xl font-display font-extrabold text-[#28BD6D] leading-snug mb-3 sm:mb-4 max-w-2xl mx-auto" data-id="element-43">
              Sembrá hoy la confianza que lo acompañará toda la vida
            </p>

            {/* Bridge de 4 ángulos — cada lector encuentra SU mensaje del anuncio */}
            <ul className="grid grid-cols-2 gap-2 sm:gap-2.5 mb-6 max-w-2xl mx-auto px-2" data-id="element-44">
              {['PARA EL "NO PUEDO"', 'PARA DESCUBRIR SUS SUPERPODERES', 'PARA CULTIVAR UNA MENTALIDAD DE CRECIMIENTO', 'PARA VOLVER A LA CALMA'].map(phrase => <li key={phrase} className="inline-flex items-center justify-center text-center bg-white border-2 border-coral/40 text-coral font-display font-extrabold text-[11px] sm:text-xs uppercase tracking-widest px-3 py-2 rounded-full shadow-sm" data-id="element-45">
                  {phrase}
                </li>)}
            </ul>

            <div className="relative mx-auto max-w-lg" data-id="element-46">
              <div className="absolute -inset-6 bg-gold/20 rounded-[2.5rem] blur-3xl pointer-events-none" aria-hidden="true" data-id="element-47" />
              <img src="https://cdn.magicpatterns.com/uploads/wYYpGKq9wkk9MCs8wydKUT/LANDING.png" alt="Descubre los superpoderes que hay en ti — Amor propio, Resiliencia, Mentalidad positiva, Valentía, Calma y Fuerza interior. Tu guía para descubrir y fortalecer el poder interior." width={1024} height={1280} loading="eager" fetchPriority="high" decoding="async" className="relative w-full h-auto rounded-[2rem] shadow-soft object-contain" data-id="element-48" />
            </div>

            {/* Primer CTA + precio + trust */}
            <div className="mt-6 text-center" data-id="element-49">
              <div className="flex flex-col items-center gap-3" data-id="element-50">
                {/* Precio compacto */}
                <div className="inline-flex items-baseline gap-2" data-id="element-51">
                  <span className="text-sm sm:text-base font-display font-bold text-foreground/50 line-through decoration-2 decoration-coral" data-id="element-52">
                    $30.000
                  </span>
                  <span className="font-display font-extrabold text-2xl sm:text-3xl text-coral leading-none" data-id="element-53">
                    $14.999
                  </span>
                  <span className="inline-flex items-center bg-gold/30 text-foreground text-[10px] font-display font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full" data-id="element-54">
                    50% OFF
                  </span>
                </div>
                <p className="text-xs text-muted-foreground -mt-1" data-id="element-55">
                  Pago único · ⚡ Acceso inmediato post-compra ⚡
                </p>

                <a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" className={ctaPrimary} onClick={() => handleInitiateCheckout('hero-primer-cta')} data-id="element-56">
                  Quiero la colección completa
                </a>

                <PaymentTrust className="mt-2" data-id="element-57" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== BLOQUE 2.5: BENEFICIOS RÁPIDOS ========== */}
      <section className="bg-warm-bg/60 border-y border-gold/20" data-id="element-58">
        <div className="container mx-auto px-4 md:px-6 py-8 md:py-10 max-w-4xl" data-id="element-59">
          <div className="reveal text-center" data-id="element-60">
            <p className="text-coral text-xs sm:text-sm font-bold uppercase tracking-widest mb-4" data-id="element-61">
              Tu hijo va a desarrollar
            </p>
            <ul className="flex flex-wrap items-center justify-center gap-2 sm:gap-3" data-id="element-62">
              {[{
              icon: '❤️',
              label: 'Amor propio'
            }, {
              icon: '⭐',
              label: 'Autoestima'
            }, {
              icon: '💛',
              label: 'Calma'
            }, {
              icon: '🛡️',
              label: 'Confianza'
            }, {
              icon: '🌱',
              label: 'Empatía'
            }, {
              icon: '🧠',
              label: 'Regulación emocional'
            }, {
              icon: '💪',
              label: 'Resiliencia'
            }, {
              icon: '🌟',
              label: 'Valentía'
            }].map(b => <li key={b.label} className="inline-flex items-center gap-2 bg-white rounded-full pl-2 pr-4 py-1.5 shadow-sm border border-gold/30 hover:-translate-y-0.5 hover:shadow-md transition-all duration-300" data-id="element-63">
                  <span className="w-7 h-7 rounded-full bg-pastel-yellow flex items-center justify-center text-base shrink-0" aria-hidden="true" data-id="element-64">
                    {b.icon}
                  </span>
                  <span className="font-display font-bold text-xs sm:text-sm text-foreground whitespace-nowrap" data-id="element-65">
                    {b.label}
                  </span>
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* ========== BLOQUE 6.5: ANTES / DESPUÉS (movido arriba para impacto inmediato) ========== */}
      <section id="transformacion" className="bg-warm-bg" data-id="element-66">
        <div className="container mx-auto px-4 md:px-6 py-10 md:py-14" data-id="element-67">
          <div className="space-y-6 md:space-y-8" data-id="element-68">
            <div className="max-w-3xl mx-auto text-center reveal" data-id="element-69">
              <p className="text-coral text-xs sm:text-sm font-bold uppercase tracking-widest mb-3" data-id="element-70">
                La transformación de Ernesto
              </p>
              <h2 className="font-display font-extrabold text-3xl md:text-4xl text-foreground leading-tight" data-id="element-71">
                Cuando creía que no podía…{' '}
                <span className="text-coral" data-id="element-72">y descubrió que sí</span> ✨
              </h2>
            </div>
            {[{
            antes: imgBerrinches,
            antesAlt: 'Ernesto frustrado mirando los cordones desatados, sin saber qué hacer',
            despues: imgDespuesCordones,
            despuesAlt: 'Papá enseñándole a Ernesto a atarse los zapatos, ambos sonriendo',
            superpoder: 'Pedir ayuda + Autonomía',
            tagBg: 'bg-pastel-blue',
            antesText: '«No puedo. Es muy difícil. Yo solo no sé.»',
            despuesText: '«Si pido ayuda, también soy fuerte. Voy a aprender.»',
            insight: 'Ernesto descubre que pedir ayuda no lo hace más débil, lo hace más sabio. Y al practicar, el desafío deja de ser imposible.'
          }, {
            antes: imgFrasesDuelen,
            antesAlt: 'Ernesto solo en el parque, mirando a otros niños jugar a la distancia',
            despues: imgDespuesParque,
            despuesAlt: 'Ernesto sonriendo en el parque rodeado de sus amigos, integrado al grupo',
            despuesObjectPosition: 'object-top scale-[1.15] origin-top',
            superpoder: 'Autoestima + Pertenencia',
            tagBg: 'bg-pastel-pink',
            antesText: '«Todos son mejores que yo. No encajo en ningún lado.»',
            despuesText: '«No tengo que ser el más rápido ni el mejor. Tal como soy, valgo un montón.»',
            insight: 'Ernesto sonríe — no porque sea el más rápido, no porque sea el mejor — sino porque entiende algo importante: no tiene que cambiar nada para ser valioso'
          }].map((pair, i) => <article key={i} className="reveal bg-card rounded-3xl shadow-card overflow-hidden" data-id="element-73">
                <div className="grid md:grid-cols-[1fr_auto_1fr] items-stretch" data-id="element-74">
                  {/* ANTES */}
                  <div className="relative" data-id="element-75">
                    <div className="absolute top-4 left-4 z-10 inline-flex items-center gap-1.5 bg-coral text-white text-xs font-display font-extrabold uppercase tracking-widest px-3 py-1.5 rounded-full shadow-coral" data-id="element-76">
                      <span aria-hidden="true" data-id="element-77">😔</span> Antes
                    </div>
                    <div className="relative aspect-[4/3] overflow-hidden bg-muted" data-id="element-78">
                      <img src={pair.antes} alt={pair.antesAlt} loading="lazy" decoding="async" width={600} height={450} className="w-full h-full object-cover saturate-[0.55] brightness-95" data-id="element-79" />
                      <div className="absolute inset-0 bg-foreground/10 mix-blend-multiply" data-id="element-80" />
                    </div>
                    <div className="p-5 sm:p-6" data-id="element-81">
                      <p className="font-display italic text-foreground/75 leading-relaxed" data-id="element-82">
                        "{pair.antesText}"
                      </p>
                    </div>
                  </div>

                  {/* Flecha conectora */}
                  <div className="flex md:flex-col items-center justify-center px-4 py-3 md:py-0 md:px-2 bg-gradient-to-r md:bg-gradient-to-b from-coral/5 via-gold/10 to-mint/10" data-id="element-83">
                    <div className="w-12 h-12 rounded-full bg-gold flex items-center justify-center shadow-gold animate-pulse-soft" data-id="element-84">
                      <span className="text-2xl md:rotate-90" aria-hidden="true" data-id="element-85">
                        →
                      </span>
                    </div>
                  </div>

                  {/* DESPUÉS */}
                  <div className="relative bg-pastel-yellow/30" data-id="element-86">
                    <div className="absolute top-4 right-4 z-10 inline-flex items-center gap-1.5 bg-mint text-foreground text-xs font-display font-extrabold uppercase tracking-widest px-3 py-1.5 rounded-full shadow-card" data-id="element-87">
                      <span aria-hidden="true" data-id="element-88">✨</span> Después
                    </div>
                    <div className="relative aspect-[4/3] overflow-hidden" data-id="element-89">
                      <div className="absolute -inset-2 bg-gold/25 blur-2xl" aria-hidden="true" data-id="element-90" />
                      <img src={pair.despues} alt={pair.despuesAlt} loading="lazy" decoding="async" width={600} height={450} className={`relative w-full h-full object-cover ${pair.despuesObjectPosition || ''}`} data-id="element-91" />
                    </div>
                    <div className="p-5 sm:p-6" data-id="element-92">
                      <p className="font-display font-bold text-foreground leading-relaxed" data-id="element-93">
                        "{pair.despuesText}"
                      </p>
                    </div>
                  </div>
                </div>

                {/* Pie con superpoder activado */}
                <div className="border-t border-border bg-warm-bg px-5 sm:px-8 py-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5" data-id="element-94">
                  <span className={`shrink-0 inline-flex items-center gap-2 ${pair.tagBg} text-foreground font-display font-extrabold text-sm px-4 py-2 rounded-full`} data-id="element-95">
                    🦸‍♂️ Superpoder activado: {pair.superpoder}
                  </span>
                  <p className="text-sm sm:text-base text-foreground/80 leading-relaxed" data-id="element-96">
                    {pair.insight}
                  </p>
                </div>
              </article>)}
          </div>

          {/* Mini-pairs grid — más momentos de transformación cotidiana */}
          <div className="mt-10 max-w-5xl mx-auto reveal" data-id="element-97">
            <p className="text-center text-coral text-xs sm:text-sm font-bold uppercase tracking-widest mb-5" data-id="element-98">
              + Otros momentos del día a día
            </p>
            {(() => {
            const miniPairs = [{
              antes: '¡No me sale!',
              despues: 'Respiramos juntos. Lo voy a intentar otra vez.'
            }, {
              antes: 'Me equivoqué… no sirvo para nada.',
              despues: 'Me equivoqué, aprendo y vuelvo a intentar.'
            }, {
              antes: '¿Por qué no soy como ellos?',
              despues: 'Tal como soy, valgo un montón.'
            }, {
              antes: 'No quiero hacerlo, me da miedo.',
              despues: 'Soy valiente. El miedo no me detiene.'
            }, {
              antes: 'Nadie me entiende.',
              despues: 'Pongo en palabras lo que siento.'
            }];
            // Duplicamos para loop sin saltos.
            const looped = [...miniPairs, ...miniPairs];
            return <div className="pairs-marquee" role="region" aria-label="Frases antes y después que dirán tus hijos" data-id="element-99">
                  <div className="pairs-marquee__track" data-id="element-100">
                    {looped.map((p, i) => <div key={i} className="pairs-marquee__card bg-card rounded-2xl shadow-card overflow-hidden grid grid-cols-[1fr_auto_1fr] items-stretch" aria-hidden={i >= miniPairs.length ? 'true' : undefined} data-id="element-101">
                        <div className="p-4 sm:p-5 bg-coral/5" data-id="element-102">
                          <p className="text-[10px] font-display font-extrabold uppercase tracking-widest text-coral mb-1.5" data-id="element-103">
                            😔 Antes
                          </p>
                          <p className="font-display italic text-foreground/75 text-sm sm:text-base leading-snug" data-id="element-104">
                            "{p.antes}"
                          </p>
                        </div>
                        <div className="flex items-center justify-center px-2 bg-gradient-to-b from-coral/10 to-mint/10" data-id="element-105">
                          <span className="text-gold text-xl font-extrabold" aria-hidden="true" data-id="element-106">
                            →
                          </span>
                        </div>
                        <div className="p-4 sm:p-5 bg-pastel-yellow/30" data-id="element-107">
                          <p className="text-[10px] font-display font-extrabold uppercase tracking-widest text-foreground/70 mb-1.5" data-id="element-108">
                            ✨ Después
                          </p>
                          <p className="font-display font-bold text-foreground text-sm sm:text-base leading-snug" data-id="element-109">
                            "{p.despues}"
                          </p>
                        </div>
                      </div>)}
                  </div>
                  <div className="pairs-marquee__fade pairs-marquee__fade--left" aria-hidden="true" data-id="element-110" />
                  <div className="pairs-marquee__fade pairs-marquee__fade--right" aria-hidden="true" data-id="element-111" />
                </div>;
          })()}
          </div>

          <div className="reveal mt-10 max-w-2xl mx-auto text-center" data-id="element-112">
            <p className="font-display font-extrabold text-xl md:text-2xl text-foreground leading-tight" data-id="element-113">
              Imaginá ver <span className="text-coral" data-id="element-114">ese cambio</span> en tu
              propio hijo. 💛
            </p>
          </div>
        </div>
      </section>

      {/* ========== BLOQUE 4: AGITACIÓN ========== */}
      <section className="bg-sky" data-id="element-115">
        <div className="container mx-auto px-4 md:px-6 py-10 md:py-12" data-id="element-116">
          <div className="max-w-3xl mx-auto text-center reveal" data-id="element-117">
            <p className="text-coral text-xs sm:text-sm font-bold uppercase tracking-widest mb-3" data-id="element-118">
              Antes de seguir, una verdad importante
            </p>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl text-foreground leading-tight" data-id="element-119">
              Tu hijo/a no se porta mal.{' '}
              <span className="text-coral" data-id="element-120">Está aprendiendo a sentir.</span>
            </h2>
            <p className="mt-4 text-base sm:text-lg text-foreground/80 leading-relaxed" data-id="element-121">
              Cuando explota, se frustra o se cierra, no lo hace para hacerte la
              vida imposible. Lo hace por falta de herramientas para volver solo
              a la calma. Herramientas que, quizás, vos tampoco tuviste de
              chico/a...
            </p>
            <p className="mt-5 text-lg sm:text-xl font-semibold text-foreground" data-id="element-122">
              No estás sol@. Y no es tu culpa. 💛
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-5 max-w-3xl mx-auto" data-id="element-123">
            {[{
            img: imgCulpaSilenciosa,
            alt: 'Madre abrazando con ternura a su hijo pequeño en la habitación',
            title: 'Culpa silenciosa',
            desc: 'Leés sobre crianza respetuosa, pero en la crisis se te olvida todo. Y duele.',
            accent: 'border-mint/60'
          }].map((c, i) => <article key={i} className={`reveal bg-card rounded-3xl shadow-card border-t-4 ${c.accent} hover:-translate-y-1 transition-transform duration-300 overflow-hidden flex flex-col`} style={{
            transitionDelay: `${i * 80}ms`
          }} data-id="element-124">
                <div className="relative aspect-[4/3] overflow-hidden bg-warm-bg" data-id="element-125">
                  <img src={c.img} alt={c.alt} loading="lazy" decoding="async" width={600} height={450} className="w-full h-full object-cover" data-id="element-126" />
                </div>
                <div className="p-6" data-id="element-127">
                  <h3 className="font-display font-extrabold text-xl text-foreground" data-id="element-128">
                    {c.title}
                  </h3>
                  <p className="mt-2 text-muted-foreground leading-relaxed" data-id="element-129">
                    {c.desc}
                  </p>
                </div>
              </article>)}
          </div>

          <div className="reveal mt-12 max-w-2xl mx-auto text-center" data-id="element-130">
            <p className="font-display font-extrabold text-2xl md:text-3xl text-foreground leading-tight" data-id="element-131">
              La buena noticia:{' '}
              <span className="text-coral" data-id="element-132">su cerebro está listo</span> para
              cambiar. <span className="text-3xl" data-id="element-133">⚡</span>
            </p>
          </div>
        </div>
      </section>

      {/* ========== BLOQUE 4.5: VENTANA NEUROLÓGICA ========== */}
      <section className="bg-card relative overflow-hidden" data-id="element-134">
        <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[40rem] h-[40rem] bg-gold/10 rounded-full blur-3xl pointer-events-none" aria-hidden="true" data-id="element-135" />
        <div className="container mx-auto px-4 md:px-6 py-14 md:py-20 relative" data-id="element-136">
          <div className="max-w-3xl mx-auto text-center reveal" data-id="element-137">
            <p className="inline-flex items-center gap-2 text-coral text-xs sm:text-sm font-bold uppercase tracking-widest mb-4 bg-coral/10 px-3 py-1.5 rounded-full" data-id="element-138">
              🧠 Y la ciencia lo confirma
            </p>
            <h2 className="font-display font-extrabold text-3xl md:text-5xl text-foreground leading-tight" data-id="element-139">
              Entre los 3 y los 10 años existe una{' '}
              <span className="text-coral" data-id="element-140">ventana única</span> para sembrar
              autoestima.
            </h2>
            <p className="mt-5 text-base sm:text-lg text-foreground/80 leading-relaxed max-w-2xl mx-auto" data-id="element-141">
              En esta etapa el cerebro de tu hijo/a forma las creencias que lo
              acompañarán toda la vida. Lo que escucha hoy —de vos, de un
              cuento, de una canción— se convierte en su{' '}
              <strong className="text-foreground" data-id="element-142">voz interior mañana</strong>.
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-3 gap-4 max-w-4xl mx-auto" data-id="element-143">
            {[{
            icon: '🌱',
            stat: '3–10',
            label: 'AÑOS',
            desc: 'La ventana de oro donde se cimientan la autoestima y la regulación emocional.',
            bg: 'bg-pastel-mint/40',
            border: 'border-mint/50'
          }, {
            icon: '💭',
            stat: '90%',
            label: 'DE LAS CREENCIAS',
            desc: 'Que tu hijo tendrá de adulto sobre sí mismo se forman antes de los 10 años.',
            bg: 'bg-pastel-yellow/40',
            border: 'border-gold/50'
          }, {
            icon: '⚡',
            stat: '10 min',
            label: 'POR NOCHE',
            desc: 'Es todo lo que hace falta para empezar a reescribir su diálogo interno.',
            bg: 'bg-pastel-peach/40',
            border: 'border-coral/40'
          }].map((s, i) => <div key={i} className={`reveal ${s.bg} ${s.border} border rounded-3xl p-6 text-center hover:-translate-y-1 transition-transform duration-300`} style={{
            transitionDelay: `${i * 80}ms`
          }} data-id="element-144">
                <div className="text-3xl mb-2" aria-hidden="true" data-id="element-145">
                  {s.icon}
                </div>
                <p className="font-display font-extrabold text-4xl sm:text-5xl text-coral leading-none" data-id="element-146">
                  {s.stat}
                </p>
                <p className="mt-1.5 text-[10px] sm:text-xs font-display font-extrabold uppercase tracking-widest text-foreground/60" data-id="element-147">
                  {s.label}
                </p>
                <p className="mt-3 text-sm text-foreground/80 leading-relaxed" data-id="element-148">
                  {s.desc}
                </p>
              </div>)}
          </div>

          <div className="reveal mt-12 max-w-2xl mx-auto" data-id="element-149">
            <div className="bg-warm-bg rounded-3xl p-6 sm:p-8 border-l-4 border-coral shadow-sm" data-id="element-150">
              <p className="font-display text-lg sm:text-xl text-foreground/90 leading-relaxed" data-id="element-151">
                Cada cuento que le leas y cada canción mindset que escuche hoy
                es{' '}
                <strong className="text-coral" data-id="element-152">
                  una semilla que florece toda la vida
                </strong>
                . No estás llenando un rato libre — estás programando el adulto
                seguro, valiente y amado por sí mismo que tu hijo/a será mañana.
              </p>
            </div>
          </div>

          <div className="reveal mt-10 text-center" data-id="element-153"></div>
        </div>
      </section>

      {/* ========== BLOQUE 5: SOLUCIÓN ========== */}
      <section className="bg-warm-bg" data-id="element-154">
        <div className="container mx-auto px-4 md:px-6 py-10 md:py-14 grid md:grid-cols-2 gap-10 md:gap-12 items-center" data-id="element-155">
          {/* Copy: objeción tiempo + promesa de método corto y constante */}
          <div className="reveal order-1" data-id="element-156">
            <p className="inline-flex items-center gap-2 text-coral text-xs sm:text-sm font-bold uppercase tracking-widest mb-4 bg-coral/10 px-3 py-1.5 rounded-full" data-id="element-157">
              ⏱️ Pensado para padres reales
            </p>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl text-foreground leading-tight" data-id="element-158">
              Entre el trabajo, la cena y mil tareas más…{' '}
              <span className="text-coral" data-id="element-159">estás al límite.</span>
            </h2>
            <p className="mt-5 text-base sm:text-lg text-foreground/80 leading-relaxed" data-id="element-160">
              Lo último que necesitás es{' '}
              <strong className="text-foreground" data-id="element-161">
                otro compromiso de una hora
              </strong>{' '}
              que se te derrumbe en la segunda semana.
            </p>
            <p className="mt-4 text-base sm:text-lg text-foreground/80 leading-relaxed" data-id="element-162">
              Por eso esta colección está pensada distinto:
            </p>
            <ul className="mt-5 space-y-3" data-id="element-163">
              <li className="flex items-start gap-3 bg-card rounded-2xl p-4 shadow-sm border-l-4 border-mint" data-id="element-164">
                <span className="shrink-0 w-9 h-9 rounded-full bg-pastel-mint flex items-center justify-center text-lg" aria-hidden="true" data-id="element-165">
                  🌙
                </span>
                <p className="text-foreground/85 leading-snug" data-id="element-166">
                  <strong className="text-foreground" data-id="element-167">La Hora Sagrada:</strong>{' '}
                  un cuento por noche, los{' '}
                  <strong className="text-coral" data-id="element-168">
                    10 minutos antes de dormir
                  </strong>{' '}
                  que se convierten en su momento favorito del día — y en tu
                  pausa de conexión emocional.
                </p>
              </li>
              <li className="flex items-start gap-3 bg-card rounded-2xl p-4 shadow-sm border-l-4 border-coral" data-id="element-169">
                <span className="shrink-0 w-9 h-9 rounded-full bg-pastel-peach flex items-center justify-center text-lg" aria-hidden="true" data-id="element-170">
                  💛
                </span>
                <p className="text-foreground/85 leading-snug" data-id="element-171">
                  <strong className="text-foreground" data-id="element-172">
                    Lo suficientemente práctica
                  </strong>{' '}
                  como para construir una confianza emocional{' '}
                  <strong className="text-coral" data-id="element-173">real y duradera</strong> en tu
                  hijo/a — sin presión, sin culpas.
                </p>
              </li>
            </ul>
          </div>

          {/* Video */}
          <div className="reveal order-2" data-id="element-174">
            <div className="relative" data-id="element-175">
              <div className="absolute -inset-4 bg-coral/15 rounded-[2rem] blur-2xl pointer-events-none" aria-hidden="true" data-id="element-176" />
              <video src={videoPromo} poster={heroImg} preload="none" controls playsInline className="relative rounded-[2rem] shadow-soft w-full h-auto object-cover max-h-[600px] mx-auto" data-id="element-177" />
            </div>
          </div>
        </div>
      </section>

      {/* ========== BLOQUE 6: 9 SUPERPODERES ========== */}
      <section id="superpoderes" className="bg-gradient-soft" data-id="element-178">
        <div className="container mx-auto px-4 md:px-6 py-10 md:py-14" data-id="element-179">
          <div className="max-w-3xl mx-auto text-center reveal" data-id="element-180">
            <h2 className="font-display font-extrabold text-3xl md:text-4xl text-foreground" data-id="element-181">
              Los 9 superpoderes que tu hijo descubrirá en sí mismo 🦸‍♂️
            </h2>
            <p className="mt-4 text-lg text-muted-foreground" data-id="element-182">
              Cada superpoder es una habilidad emocional y psicológica real,
              disfrazada de aventura.
            </p>
            {/* Rehook ad "superhéroe real ≠ rayos láser/capas" */}
            <div className="mt-6 inline-block bg-card rounded-2xl px-5 py-4 shadow-sm border border-gold/30 max-w-xl" data-id="element-183">
              <p className="font-display text-base sm:text-lg text-foreground/90 leading-snug" data-id="element-184">
                Para sentirse superhéroe,{' '}
                <span className="font-bold text-coral" data-id="element-185">
                  no necesita rayos láser, capas ni espadas luminosas
                </span>
                . Sus verdaderos superpoderes son internos — y los entrena con
                cada cuento.
              </p>
            </div>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-186">
            {superpowers.map((s, i) => <article key={s.n} className={`reveal ${s.bg} rounded-3xl p-6 shadow-card hover:-translate-y-1 transition-transform duration-300 group`} style={{
            transitionDelay: `${i % 3 * 80}ms`
          }} data-id="element-187">
                <div className="flex items-start justify-between gap-3" data-id="element-188">
                  <div className="relative shrink-0" data-id="element-189">
                    <div className="absolute -inset-1 bg-foreground/5 rounded-xl blur-md" aria-hidden="true" data-id="element-190" />
                    <img src={s.cover} alt={`Portada del cuento ${s.title}`} loading="lazy" decoding="async" width={96} height={96} className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-xl object-cover shadow-card ring-2 ring-card/80 group-hover:scale-105 group-hover:rotate-[-2deg] transition-transform duration-300" data-id="element-191" />
                  </div>
                  <span className="font-display font-extrabold text-foreground/30 text-3xl leading-none" data-id="element-192">
                    #{s.n}
                  </span>
                </div>
                <h3 className="mt-5 font-display font-extrabold text-xl text-foreground" data-id="element-193">
                  Superpoder #{s.n}: {s.title}
                </h3>
                <p className="mt-2 text-foreground/80 leading-relaxed" data-id="element-194">
                  {s.desc}
                </p>
              </article>)}
          </div>
        </div>
      </section>

      {/* ========== BLOQUE 7: ENTREGABLES ========== */}
      <section id="entregables" className="bg-card relative overflow-hidden" data-id="element-195">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-gold/10 rounded-full blur-3xl" aria-hidden="true" data-id="element-196" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-coral/10 rounded-full blur-3xl" aria-hidden="true" data-id="element-197" />
        <div className="container mx-auto px-4 md:px-6 py-10 md:py-14 relative" data-id="element-198">
          <div className="max-w-3xl mx-auto text-center reveal" data-id="element-199">
            <p className="inline-flex items-center gap-2 text-coral text-xs sm:text-sm font-bold uppercase tracking-widest mb-4 bg-coral/10 px-3 py-1.5 rounded-full" data-id="element-200">
              🎁 Todo lo que recibirás hoy
            </p>
            <h2 className="font-display font-extrabold text-3xl md:text-5xl text-foreground leading-tight" data-id="element-201">
              Un kit completo para acompañar a tu hijo en su{' '}
              <span className="text-coral" data-id="element-202">progreso personal</span>
            </h2>
            <p className="mt-4 text-lg text-muted-foreground" data-id="element-203">
              Herramientas diseñadas para acompañar la educación emocional de
              los más chiquitos. Material listo para usar y disfrutar.
            </p>
          </div>

          {/* Value Stack */}
          <div className="mt-10 max-w-2xl mx-auto bg-warm-bg rounded-3xl p-6 sm:p-8 shadow-soft border border-gold/20 reveal" data-id="element-204">
            <p className="text-xs sm:text-sm font-bold text-muted-foreground uppercase tracking-wider mb-5 text-center" data-id="element-205">
              Lo que muchos pagarían por separado:
            </p>
            <ul className="space-y-3" data-id="element-206">
              <li className="flex items-center justify-between gap-3 p-3 sm:p-4 bg-white rounded-xl shadow-sm border border-coral/20" data-id="element-207">
                <span className="font-display font-bold text-foreground flex items-center gap-2 text-sm sm:text-base" data-id="element-208">
                  📚 Colección de 10 cuentos
                </span>
                <div className="flex flex-col items-end shrink-0 gap-1" data-id="element-209">
                  <span className="font-display font-bold text-xs sm:text-sm text-muted-foreground line-through decoration-2 decoration-coral" data-id="element-210">
                    Valor $30.000
                  </span>
                  <span className="inline-flex items-center text-[10px] sm:text-xs font-extrabold text-white uppercase tracking-wider bg-gradient-to-br from-coral to-[#E05252] px-2.5 py-1 rounded-full shadow-coral whitespace-nowrap" data-id="element-211">
                    🎁 GRATIS HOY
                  </span>
                </div>
              </li>
              <li className="flex items-center justify-between gap-3 p-3 sm:p-4 bg-white rounded-xl shadow-sm border border-coral/20" data-id="element-212">
                <span className="font-display font-bold text-foreground flex items-center gap-2 text-sm sm:text-base" data-id="element-213">
                  🎵 10 Canciones Mindset
                </span>
                <div className="flex flex-col items-end shrink-0 gap-1" data-id="element-214">
                  <span className="font-display font-bold text-xs sm:text-sm text-muted-foreground line-through decoration-2 decoration-coral" data-id="element-215">
                    Valor $75.000
                  </span>
                  <span className="inline-flex items-center text-[10px] sm:text-xs font-extrabold text-white uppercase tracking-wider bg-gradient-to-br from-coral to-[#E05252] px-2.5 py-1 rounded-full shadow-coral whitespace-nowrap" data-id="element-216">
                    🎁 GRATIS HOY
                  </span>
                </div>
              </li>
              <li className="flex items-center justify-between gap-3 p-3 sm:p-4 bg-white rounded-xl shadow-sm border border-coral/20" data-id="element-217">
                <span className="font-display font-bold text-foreground flex items-center gap-2 text-sm sm:text-base" data-id="element-218">
                  ✏️ Cuadernillo de actividades
                </span>
                <div className="flex flex-col items-end shrink-0 gap-1" data-id="element-219">
                  <span className="font-display font-bold text-xs sm:text-sm text-muted-foreground line-through decoration-2 decoration-coral" data-id="element-220">
                    Valor $10.000
                  </span>
                  <span className="inline-flex items-center text-[10px] sm:text-xs font-extrabold text-white uppercase tracking-wider bg-gradient-to-br from-coral to-[#E05252] px-2.5 py-1 rounded-full shadow-coral whitespace-nowrap" data-id="element-221">
                    🎁 GRATIS HOY
                  </span>
                </div>
              </li>
              <li className="flex items-center justify-between gap-3 p-3 sm:p-4 bg-white rounded-xl shadow-sm border border-coral/20" data-id="element-222">
                <span className="font-display font-bold text-foreground flex items-center gap-2 text-sm sm:text-base" data-id="element-223">
                  👨‍👩‍👧 Guía para padres
                </span>
                <div className="flex flex-col items-end shrink-0 gap-1" data-id="element-224">
                  <span className="font-display font-bold text-xs sm:text-sm text-muted-foreground line-through decoration-2 decoration-coral" data-id="element-225">
                    Valor $5.000
                  </span>
                  <span className="inline-flex items-center text-[10px] sm:text-xs font-extrabold text-white uppercase tracking-wider bg-gradient-to-br from-coral to-[#E05252] px-2.5 py-1 rounded-full shadow-coral whitespace-nowrap" data-id="element-226">
                    🎁 GRATIS HOY
                  </span>
                </div>
              </li>
            </ul>
            <div className="mt-5 pt-5 border-t-2 border-dashed border-gold/40 flex items-center justify-between" data-id="element-227">
              <span className="font-display font-extrabold text-base sm:text-lg text-foreground uppercase tracking-wide" data-id="element-228">
                Valor total:
              </span>
              <span className="font-display font-extrabold text-lg sm:text-xl text-foreground/70 line-through decoration-2 decoration-coral" data-id="element-229">
                $120.000
              </span>
            </div>
            <div className="mt-4 flex items-center justify-center" data-id="element-230">
              <span className="inline-flex items-center gap-2 bg-coral text-white font-display font-extrabold text-sm sm:text-base uppercase tracking-wider px-4 py-2 rounded-full shadow-coral animate-pulse-soft" data-id="element-231">
                🔥 AHORRÁS $105.001 · 87% OFF
              </span>
            </div>
            <p className="mt-4 text-center font-display font-extrabold text-lg sm:text-xl text-coral" data-id="element-232">
              Hoy te llevás TODO incluído. Sin costos adicionales.
            </p>
          </div>

          {/* Price Reveal */}
          <div className="mt-8 max-w-2xl mx-auto reveal" data-id="element-233">
            <div className="relative bg-gradient-to-br from-pastel-yellow to-gold/40 rounded-3xl p-6 sm:p-10 text-center shadow-gold border-2 border-gold" data-id="element-234">
              <span className="absolute -top-4 left-1/2 -translate-x-1/2 bg-coral text-white text-xs sm:text-sm font-display font-extrabold uppercase tracking-widest px-4 py-1.5 rounded-full shadow-coral whitespace-nowrap" data-id="element-235">
                🔥 50% OFF · Oferta de hoy
              </span>

              {/* Mini-stats: rating + familias */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-3 text-xs sm:text-sm text-foreground/75 mb-3" data-id="element-236">
                <span className="text-gold tracking-widest" data-id="element-237">⭐⭐⭐⭐⭐</span>
                <span className="font-display font-bold" data-id="element-238">
                  <span className="text-foreground" data-id="element-239">4.9/5</span>
                  <span className="text-foreground/40 mx-1.5" data-id="element-240">·</span>
                  <span data-id="element-241">+1.247 familias acompañadas</span>
                </span>
              </div>

              <div className="flex items-baseline justify-center gap-3" data-id="element-242">
                <span className="text-lg sm:text-2xl font-display font-bold text-foreground/60 line-through decoration-2 decoration-coral" data-id="element-243">
                  $30.000
                </span>
              </div>
              <p className="font-display font-extrabold sm:text-7xl text-coral my-2 text-[72px] animate-heartbeat origin-center" data-id="element-244">
                $14.999
              </p>
              <p className="text-xs sm:text-sm text-foreground/70 mt-4" data-id="element-245">
                Pago único · Acceso inmediato · Todos los dispositivos
              </p>
              <a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 mt-6 bg-coral text-white font-display font-extrabold rounded-full px-8 py-4 text-base sm:text-lg shadow-coral hover:scale-[1.03] hover:brightness-110 transition-all duration-300 w-full sm:w-auto" onClick={() => handleInitiateCheckout('entregables-precio-comprar')} data-id="element-246">
                Quiero la colección ahora
              </a>
              <PaymentTrust className="mt-5" data-id="element-247" />
            </div>

            {/* Credibilidad / disclaimer */}
            <div className="mt-5 bg-pastel-mint/40 border border-mint/50 rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row items-center gap-3 sm:gap-4 text-center sm:text-left" data-id="element-248">
              <span className="text-3xl shrink-0" aria-hidden="true" data-id="element-249">
                🧠
              </span>
              <p className="text-xs sm:text-sm text-foreground/80 leading-relaxed" data-id="element-250">
                <span className="font-bold text-foreground" data-id="element-251">
                  Material con enfoque psicopedagógico
                </span>{' '}
                creado para acompañar la educación emocional en casa o en el
                aula. No reemplaza la atención de un profesional de la salud
                mental.
              </p>
            </div>
          </div>

          {/* Grid de 4 productos hero */}
          <div className="mt-14 grid md:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto" data-id="element-252">
            {[{
            title: 'Colección completa de cuentos',
            subtitle: '«Los superpoderes que hay en mí»',
            desc: 'Los 10 cuentos digitales donde Ernesto descubre cada uno de los 9 superpoderes emocionales. La base de toda la metodología.',
            bullets: ['10 cuentos en PDF de alta resolución', '9 superpoderes emocionales', 'Para niños/as de 3 a 10 años'],
            img: imgColeccionCuentos,
            imgAlt: 'Colección completa de los cuentos «Los superpoderes que hay en mí»',
            bgGradient: 'from-pastel-yellow to-pastel-peach',
            glowColor: 'bg-gold/40',
            imgClass: 'object-contain p-6'
          }, {
            title: 'Canciones Mindset',
            subtitle: 'Ernesto y sus Superpoderes',
            desc: 'Una colección musical de desarrollo personal infantil. 10 canciones para que el aprendizaje emocional acompañe a tu hijo en todo momento.',
            bullets: ['10 canciones mindset originales', 'Una por cada superpoder + tema central', 'Audio de alta calidad para escuchar siempre'],
            img: imgCancionesMindset,
            imgAlt: 'Caja de la colección musical «10 Canciones Mindset: Ernesto y sus Superpoderes» y un niño escuchando con auriculares',
            bgGradient: 'from-pastel-blue to-pastel-lilac',
            glowColor: 'bg-coral/30',
            imgClass: 'object-cover'
          }, {
            title: 'Cuadernillo de actividades',
            subtitle: 'Desarrollo personal infantil',
            desc: 'Ejercicios, dinámicas y actividades guiadas para que tu hijo entrene cada superpoder con sus propias manos.',
            bullets: ['Actividades para los 9 superpoderes', 'Refuerza la autoestima y el desarrollo de los superpoderes personales', 'Listo para imprimir en casa'],
            img: imgCuadernilloCover,
            imgAlt: 'Cuadernillo de actividades con un corazón abrazado en la portada',
            bgGradient: 'from-pastel-aqua to-pastel-mint',
            glowColor: 'bg-gold/30',
            imgClass: 'object-contain p-6'
          }, {
            title: 'Guía para padres',
            subtitle: '«Cría niños conscientes»',
            desc: 'El acompañamiento que te falta. Reflexiones, preguntas y actividades para usar el material junto a tu hijo, paso a paso.',
            bullets: ['Fortalece el vínculo familiar', 'Desarrolla la inteligencia emocional', 'Herramientas prácticas para el día a día'],
            img: imgGuiaPadres,
            imgAlt: 'Guía para padres «Cría niños conscientes» con un papá leyendo junto a su hija',
            bgGradient: 'from-pastel-lavender to-pastel-pink',
            glowColor: 'bg-coral/30',
            imgClass: 'object-contain p-6'
          }].map((item, i) => <article key={i} className={`reveal bg-gradient-to-br ${item.bgGradient} rounded-3xl shadow-card hover:-translate-y-1 hover:shadow-soft transition-all duration-300 overflow-hidden flex flex-col group`} style={{
            transitionDelay: `${i % 2 * 80}ms`
          }} data-id="element-253">
                {/* Imagen del producto */}
                <div className="relative aspect-[4/3] overflow-hidden bg-card/40" data-id="element-254">
                  <div className={`absolute inset-0 ${item.glowColor} blur-3xl opacity-60`} aria-hidden="true" data-id="element-255" />
                  <img src={item.img} alt={item.imgAlt} loading="lazy" decoding="async" width={600} height={450} className={`relative w-full h-full ${item.imgClass} group-hover:scale-105 transition-transform duration-500`} data-id="element-256" />
                  <span className={`absolute top-4 left-4 inline-flex items-center gap-1.5 backdrop-blur-sm text-[11px] font-display font-extrabold uppercase tracking-wider px-3 py-1.5 rounded-full shadow-card ${i === 0 ? 'bg-card/95 text-foreground' : 'bg-coral text-white animate-pulse-soft'}`} data-id="element-257">
                    {i === 0 ? '✨ LO QUE COMPRÁS' : '🎁 DE REGALO'}
                  </span>
                </div>
                {/* Contenido */}
                <div className="p-6 sm:p-7 flex-1 flex flex-col bg-card/80 backdrop-blur-sm" data-id="element-258">
                  <h3 className="font-display font-extrabold text-xl sm:text-2xl text-foreground leading-tight" data-id="element-259">
                    {item.title}
                  </h3>
                  <p className="mt-1 text-coral font-display font-bold text-sm" data-id="element-260">
                    {item.subtitle}
                  </p>
                  <p className="mt-3 text-foreground/75 leading-relaxed text-sm sm:text-base flex-1" data-id="element-261">
                    {item.desc}
                  </p>
                  <ul className="mt-4 space-y-2 pt-4 border-t border-border/50" data-id="element-262">
                    {item.bullets.map((b, j) => <li key={j} className="flex items-start gap-2 text-sm text-foreground/80" data-id="element-263">
                        <span className="shrink-0 w-5 h-5 rounded-full bg-mint flex items-center justify-center text-foreground text-[10px] font-bold mt-0.5" data-id="element-264">
                          ✓
                        </span>
                        <span data-id="element-265">{b}</span>
                      </li>)}
                  </ul>
                </div>
              </article>)}
          </div>

          {/* Por qué funciona */}
          <div className="mt-20 max-w-4xl mx-auto reveal" data-id="element-266">
            <div className="text-center mb-10" data-id="element-267">
              <h3 className="font-display font-extrabold text-2xl md:text-4xl text-foreground" data-id="element-268">
                ¿Por qué este combo funciona tan profundo?
              </h3>
              <p className="mt-3 text-lg text-muted-foreground" data-id="element-269">
                Porque llegamos a tu hijo por tres caminos al mismo tiempo:
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6" data-id="element-270">
              <div className="bg-pastel-blue/30 rounded-3xl p-6 text-center border border-pastel-blue/50" data-id="element-271">
                <div className="text-4xl mb-4" data-id="element-272">👂</div>
                <h4 className="font-display font-extrabold text-xl mb-2 text-foreground" data-id="element-273">
                  OYEN
                </h4>
                <p className="text-sm text-foreground/80 leading-relaxed" data-id="element-274">
                  Las canciones entran por el oído. El ritmo y la melodía hacen
                  que las palabras de amor propio se queden.
                </p>
              </div>
              <div className="bg-pastel-yellow/30 rounded-3xl p-6 text-center border border-pastel-yellow/50" data-id="element-275">
                <div className="text-4xl mb-4" data-id="element-276">👀</div>
                <h4 className="font-display font-extrabold text-xl mb-2 text-foreground" data-id="element-277">
                  VEN
                </h4>
                <p className="text-sm text-foreground/80 leading-relaxed" data-id="element-278">
                  Las ilustraciones de los cuentos los acompañan. Imaginan,
                  recuerdan, asocian.
                </p>
              </div>
              <div className="bg-pastel-mint/30 rounded-3xl p-6 text-center border border-pastel-mint/50" data-id="element-279">
                <div className="text-4xl mb-4" data-id="element-280">✋</div>
                <h4 className="font-display font-extrabold text-xl mb-2 text-foreground" data-id="element-281">
                  VIVEN
                </h4>
                <p className="text-sm text-foreground/80 leading-relaxed" data-id="element-282">
                  En el cuadernillo dibujan, escriben, juegan. El aprendizaje
                  pasa por sus manos.
                </p>
              </div>
            </div>
            <div className="mt-10 bg-warm-bg rounded-2xl p-6 sm:p-8 border-l-4 border-coral text-center sm:text-left shadow-sm" data-id="element-283">
              <p className="font-display text-lg sm:text-xl text-foreground/90 leading-relaxed" data-id="element-284">
                Las heridas de la infancia perduran porque entraron por varios
                sentidos a la vez. Esta vez, lo que entra por varios sentidos es
                amor propio, valentía y confianza.{' '}
                <span className="font-bold italic text-coral" data-id="element-285">
                  Eso es lo que queda grabado para siempre.
                </span>
              </p>
            </div>
          </div>

          {/* CTA puente hacia testimonios */}
          <div className="mt-16 max-w-2xl mx-auto text-center reveal" data-id="element-286">
            <a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" className={ctaPrimary} onClick={() => handleInitiateCheckout('entregables-descubrir-superpoderes')} data-id="element-287">
              Quiero que mi hijo descubra sus superpoderes
            </a>
            <p className="mt-3 text-xs text-muted-foreground" data-id="element-288">
              ⚡Acceso inmediato post-compra⚡
            </p>
            <PaymentTrust className="mt-4" data-id="element-289" />
          </div>
        </div>
      </section>

      {/* ========== BLOQUE 7.5: DOBLE BENEFICIO ========== */}
      <section className="bg-warm-bg" data-id="element-290">
        <div className="container mx-auto px-4 md:px-6 py-16 md:py-20" data-id="element-291">
          <div className="max-w-3xl mx-auto text-center reveal" data-id="element-292">
            <p className="inline-flex items-center gap-2 text-coral text-xs sm:text-sm font-bold uppercase tracking-widest mb-3 bg-coral/10 px-3 py-1.5 rounded-full" data-id="element-293">
              💛 Para los dos
            </p>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl text-foreground leading-tight" data-id="element-294">
              No es solo para tu hijo/a.{' '}
              <span className="text-coral" data-id="element-295">También es para vos.</span>
            </h2>
            <p className="mt-4 text-lg text-muted-foreground" data-id="element-296">
              Los niños{' '}
              <strong className="text-foreground" data-id="element-297">
                absorben las emociones de los adultos
              </strong>{' '}
              y, sobre todo,{' '}
              <strong className="text-foreground" data-id="element-298">
                cómo les hablamos y lo que les decimos
              </strong>
              : esas palabras se convierten en su voz interior. Cuando vos tenés
              herramientas para regularte y para hablarle, tu hijo/a aprende a
              hacerlo también. Por eso este material acompaña a los dos.
            </p>
          </div>

          <div className="mt-10 grid md:grid-cols-2 gap-5 max-w-4xl mx-auto" data-id="element-299">
            {/* Para el niño/a */}
            <article className="reveal bg-card rounded-3xl p-7 shadow-card border-t-4 border-pastel-mint" data-id="element-300">
              <div className="flex items-center gap-3 mb-5" data-id="element-301">
                <div className="w-12 h-12 rounded-full bg-pastel-mint flex items-center justify-center text-2xl shadow-sm" data-id="element-302">
                  🧒
                </div>
                <h3 className="font-display font-extrabold text-2xl text-foreground" data-id="element-303">
                  Para tu hijo/a:
                </h3>
              </div>
              <ul className="space-y-3" data-id="element-304">
                <li className="flex items-start gap-3" data-id="element-305">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-mint flex items-center justify-center text-foreground text-[10px] font-bold mt-1" data-id="element-306">
                    ✓
                  </span>
                  <span className="text-foreground/85 leading-relaxed" data-id="element-307">
                    Empieza a <strong data-id="element-308">poner en palabras</strong> lo que siente
                    en vez de explotar o guardárselo.
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-309">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-mint flex items-center justify-center text-foreground text-[10px] font-bold mt-1" data-id="element-310">
                    ✓
                  </span>
                  <span className="text-foreground/85 leading-relaxed" data-id="element-311">
                    Aprende a <strong data-id="element-312">volver a la calma</strong> cuando se
                    enoja, se frustra o tiene miedo.
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-313">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-mint flex items-center justify-center text-foreground text-[10px] font-bold mt-1" data-id="element-314">
                    ✓
                  </span>
                  <span className="text-foreground/85 leading-relaxed" data-id="element-315">
                    Transforma el «no puedo» en{' '}
                    <strong data-id="element-316">«lo voy a intentar otra vez»</strong>.
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-317">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-mint flex items-center justify-center text-foreground text-[10px] font-bold mt-1" data-id="element-318">
                    ✓
                  </span>
                  <span className="text-foreground/85 leading-relaxed" data-id="element-319">
                    Construye <strong data-id="element-320">autoestima real</strong>: se mira al
                    espejo y se acepta como es.
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-321">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-mint flex items-center justify-center text-foreground text-[10px] font-bold mt-1" data-id="element-322">
                    ✓
                  </span>
                  <span className="text-foreground/85 leading-relaxed" data-id="element-323">
                    Desarrolla <strong data-id="element-324">habilidades emocionales</strong> que le
                    van a servir toda la vida.
                  </span>
                </li>
              </ul>
            </article>

            {/* Para mamá/papá */}
            <article className="reveal bg-card rounded-3xl p-7 shadow-card border-t-4 border-pastel-peach" data-id="element-325">
              <div className="flex items-center gap-3 mb-5" data-id="element-326">
                <div className="w-12 h-12 rounded-full bg-pastel-peach flex items-center justify-center text-2xl shadow-sm" data-id="element-327">
                  💛
                </div>
                <h3 className="font-display font-extrabold text-2xl text-foreground" data-id="element-328">
                  Para vos, mamá o papá:
                </h3>
              </div>
              <ul className="space-y-3" data-id="element-329">
                <li className="flex items-start gap-3" data-id="element-330">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-coral flex items-center justify-center text-white text-[10px] font-bold mt-1" data-id="element-331">
                    ✓
                  </span>
                  <span className="text-foreground/85 leading-relaxed" data-id="element-332">
                    Dejás de <strong data-id="element-333">improvisar</strong> cada crisis: ya sabés
                    qué cuento leer y qué pregunta hacer.
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-334">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-coral flex items-center justify-center text-white text-[10px] font-bold mt-1" data-id="element-335">
                    ✓
                  </span>
                  <span className="text-foreground/85 leading-relaxed" data-id="element-336">
                    Te sentís <strong data-id="element-337">menos culpable</strong> y más
                    acompañada/o, porque hacés algo concreto cada día.
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-338">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-coral flex items-center justify-center text-white text-[10px] font-bold mt-1" data-id="element-339">
                    ✓
                  </span>
                  <span className="text-foreground/85 leading-relaxed" data-id="element-340">
                    <strong data-id="element-341">Reducís los gritos</strong> y las luchas de poder —
                    sin perder autoridad.
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-342">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-coral flex items-center justify-center text-white text-[10px] font-bold mt-1" data-id="element-343">
                    ✓
                  </span>
                  <span className="text-foreground/85 leading-relaxed" data-id="element-344">
                    Creás un <strong data-id="element-345">momento diario de conexión</strong> donde
                    tu hijo/a se siente escuchado/a.
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-346">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-coral flex items-center justify-center text-white text-[10px] font-bold mt-1" data-id="element-347">
                    ✓
                  </span>
                  <span className="text-foreground/85 leading-relaxed" data-id="element-348">
                    Tenés un recurso <strong data-id="element-349">flexible</strong>, que usás a tu
                    ritmo, en casa o donde quieras.
                  </span>
                </li>
              </ul>
            </article>
          </div>

          <div className="reveal mt-10 max-w-2xl mx-auto text-center" data-id="element-350">
            <p className="font-display text-xl md:text-2xl text-foreground/90 leading-snug" data-id="element-351">
              No estás comprando solo cuentos. Estás comprando{' '}
              <span className="font-extrabold text-coral" data-id="element-352">
                un lenguaje en común
              </span>{' '}
              para hablar de lo que tu hijo/a siente.
            </p>
          </div>
        </div>
      </section>

      {/* ========== BLOQUE 8: TESTIMONIOS ========== */}
      <section id="testimonios" className="bg-sky" data-id="element-353">
        <div className="container mx-auto px-4 md:px-6 py-10 md:py-14" data-id="element-354">
          <div className="max-w-3xl mx-auto text-center reveal" data-id="element-355">
            <h2 className="font-display font-extrabold text-3xl md:text-4xl text-foreground" data-id="element-356">
              Lo que dicen las familias de niños que ya descubrieron sus
              superpoderes ❤️
            </h2>
            <p className="mt-4 text-lg text-muted-foreground" data-id="element-357">
              +1.247 familias ya han transformado su crianza.
            </p>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6" data-id="element-358">
            {testimonials.map((t, i) => <article key={i} className="reveal bg-card rounded-3xl p-6 shadow-card flex flex-col" style={{
            transitionDelay: `${i * 80}ms`
          }} data-id="element-359">
                <div className="flex items-center gap-3" data-id="element-360">
                  <div className={`w-12 h-12 rounded-full ${t.color} flex items-center justify-center font-display font-extrabold text-foreground`} data-id="element-361">
                    {t.initials}
                  </div>
                  <div data-id="element-362">
                    <p className="font-display font-bold text-foreground" data-id="element-363">
                      {t.name}
                    </p>
                    <p className="text-xs text-muted-foreground" data-id="element-364">{t.child}</p>
                  </div>
                </div>
                <p className="mt-3 text-coral" data-id="element-365">⭐⭐⭐⭐⭐</p>
                <h3 className="mt-2 font-display font-extrabold text-foreground text-base sm:text-lg leading-snug" data-id="element-366">
                  {t.title}
                </h3>
                <p className="mt-2 italic text-foreground/80 leading-relaxed text-sm sm:text-base" data-id="element-367">
                  "{t.text}"
                </p>
              </article>)}
          </div>
          <div className="reveal mt-12 grid grid-cols-2 max-w-md mx-auto gap-4 text-center" data-id="element-368">
            {[{
            big: '+1.247',
            small: 'familias'
          }, {
            big: '⭐ 4.9/5',
            small: 'satisfacción'
          }].map((s, i) => <div key={i} data-id="element-369">
                <p className="font-display font-extrabold text-2xl sm:text-3xl text-coral" data-id="element-370">
                  {s.big}
                </p>
                <p className="text-xs sm:text-sm text-muted-foreground uppercase tracking-wider mt-1" data-id="element-371">
                  {s.small}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* ========== BLOQUE 9: CIERRE DE COMPRA / URGENCIA ========== */}
      <section id="oferta" ref={ofertaRef} className="bg-gradient-warm-gold text-foreground" data-id="element-372">
        <div className="container mx-auto px-4 md:px-6 py-10 md:py-16" data-id="element-373">
          <div className="max-w-3xl mx-auto text-center" data-id="element-374">
            <p className="reveal text-coral text-xs sm:text-sm font-bold uppercase tracking-widest" data-id="element-375">
              ⏳ Confirmá tu pedido antes de que se acabe el tiempo
            </p>
            <h2 className="reveal mt-4 font-display font-extrabold text-3xl md:text-5xl leading-tight text-foreground" data-id="element-376">
              Estás a un clic de activar sus{' '}
              <span className="text-coral" data-id="element-377">superpoderes</span>
            </h2>

            {/* Countdown reservation */}
            <div className="reveal mt-10" data-id="element-378">
              <div className="relative max-w-xl mx-auto bg-card/95 rounded-3xl p-6 sm:p-8 shadow-soft border border-gold/30" data-id="element-379">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-coral text-white text-xs font-display font-bold uppercase tracking-widest px-4 py-1.5 rounded-full shadow-coral" data-id="element-380">
                  🚨 Tu lugar reservado
                </div>
                <p className="mt-4 text-center text-foreground/85 leading-relaxed" data-id="element-381">
                  Mantenemos el precio de{' '}
                  <span className="font-display font-extrabold text-coral" data-id="element-382">
                    $14.999
                  </span>{' '}
                  durante{' '}
                  <span className="font-display font-extrabold text-foreground bg-gold/40 px-1.5 rounded" data-id="element-383">
                    20 minutos
                  </span>
                  .
                </p>
                <div className="mt-6 bg-gradient-night rounded-2xl py-6" data-id="element-384">
                  <Suspense fallback={<LazyFallback minH="5rem" data-id="element-386" />} data-id="element-385">
                    <Countdown data-id="element-387" />
                  </Suspense>
                </div>
              </div>
            </div>

            {/* Mini-FAQ de cierre — resuelve las 3 últimas dudas antes del clic */}
            <ul className="reveal mt-10 max-w-xl mx-auto grid sm:grid-cols-3 gap-3 text-left" data-id="element-388">
              {[{
              icon: '📱',
              q: '¿Cómo lo recibo?',
              a: 'Por email en menos de 5 min, en cualquier dispositivo.'
            }, {
              icon: '🖨️',
              q: '¿Lo puedo imprimir?',
              a: 'Sí. Alta resolución, listo para imprimir en casa.'
            }, {
              icon: '♾️',
              q: '¿Y mañana?',
              a: 'Acceso de por vida. Sin mensualidades ni renovaciones.'
            }].map((f, i) => <li key={i} className="bg-card/80 backdrop-blur-sm rounded-2xl p-4 border border-gold/30 shadow-sm" data-id="element-389">
                  <div className="text-2xl mb-1" aria-hidden="true" data-id="element-390">
                    {f.icon}
                  </div>
                  <p className="font-display font-extrabold text-sm text-foreground leading-snug" data-id="element-391">
                    {f.q}
                  </p>
                  <p className="mt-1 text-xs text-foreground/75 leading-snug" data-id="element-392">
                    {f.a}
                  </p>
                </li>)}
            </ul>

            <div className="reveal mt-10" data-id="element-393">
              <a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center bg-coral text-white font-display font-extrabold text-lg sm:text-xl px-8 sm:px-12 py-5 rounded-full shadow-coral hover:scale-[1.03] hover:brightness-110 transition-all w-full sm:w-auto" onClick={() => handleInitiateCheckout('oferta-final-si-quiero')} data-id="element-394">
                Sí, quiero la colección completa
              </a>
              <p className="mt-3 text-sm font-bold text-foreground" data-id="element-395">
                Pago único de <span className="text-coral" data-id="element-396">$14.999</span> ·
                Acceso inmediato
              </p>
            </div>

            <p className="reveal mt-6 text-foreground/70 text-sm" data-id="element-397">
              🔒 Compra 100% protegida · 7 días de garantía
            </p>
            <PaymentTrust className="reveal mt-5" data-id="element-398" />
          </div>
        </div>
      </section>

      {/* ========== BLOQUE 10: GARANTÍA ========== */}
      <section className="bg-warm-bg" data-id="element-399">
        <div className="container mx-auto px-4 md:px-6 py-10 md:py-14" data-id="element-400">
          <div className="max-w-2xl mx-auto reveal" data-id="element-401">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-night p-8 sm:p-10 shadow-[0_10px_40px_rgba(0,0,0,0.6),0_0_0_1px_rgba(248,113,113,0.1)] border border-coral/20" data-id="element-402">
              {/* Efecto de brillo animado en el fondo */}
              <div className="absolute -top-1/2 -left-1/2 w-[200%] h-[200%] bg-[radial-gradient(circle,rgba(248,113,113,0.15)_0%,transparent_70%)] pointer-events-none animate-[spin_10s_linear_infinite]" data-id="element-403" />

              {/* Badge 7 DÍAS SIN RIESGO */}
              <div className="relative z-10 bg-gradient-to-br from-coral to-[#E05252] rounded-2xl p-6 sm:p-8 text-center border-2 border-coral/50 shadow-[0_8px_25px_rgba(248,113,113,0.5),0_0_40px_rgba(248,113,113,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] overflow-hidden mb-6" data-id="element-404">
                {/* Brillo superior del badge */}
                <div className="absolute top-0 left-0 right-0 h-[40%] bg-gradient-to-b from-white/20 to-transparent pointer-events-none" data-id="element-405" />

                <div className="relative font-display font-extrabold text-3xl sm:text-4xl text-white uppercase tracking-wider mb-2 drop-shadow-md" style={{
                textShadow: '0 2px 4px rgba(0,0,0,0.15), 0 -1px 0 rgba(255,255,255,0.3)'
              }} data-id="element-406">
                  7 DÍAS SIN RIESGO
                </div>
                <p className="relative text-white/90 font-medium text-sm sm:text-base leading-relaxed" data-id="element-407">
                  Prueba todo el contenido con total confianza.
                </p>
              </div>

              <div className="text-center relative z-10" data-id="element-408">
                <p className="text-white/80 leading-relaxed text-lg" data-id="element-409">
                  Estamos tan seguros de la calidad de este material que te
                  ofrecemos una{' '}
                  <strong className="text-white" data-id="element-410">
                    garantía incondicional de 7 días
                  </strong>
                  . Si por alguna razón sientes que la colección no es lo que
                  esperabas, te devolvemos el 100% de tu dinero sin hacer
                  preguntas.
                </p>
              </div>

              {/* Checklist de garantías */}
              <div className="relative z-10 mt-6 flex flex-col gap-2.5" data-id="element-411">
                {['Compra 100% protegida', 'Reembolso al 100% sin preguntas', 'Soporte directo por WhatsApp y email'].map(t => <div key={t} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/5 backdrop-blur-sm border-l-[3px] border-mint hover:bg-white/10 hover:translate-x-1 transition-all duration-300" data-id="element-412">
                    <span className="text-mint text-lg shrink-0 drop-shadow-[0_0_8px_rgba(34,197,94,0.5)]" aria-hidden="true" data-id="element-413">
                      ✓
                    </span>
                    <span className="text-white/90 font-display font-semibold text-sm sm:text-base" data-id="element-414">
                      {t}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== BLOQUE 11: FAQs ========== */}
      <section id="faq" className="bg-sky" data-id="element-415">
        <div className="container mx-auto px-4 md:px-6 py-10 md:py-14 max-w-3xl" data-id="element-416">
          <div className="text-center reveal" data-id="element-417">
            <h2 className="font-display font-extrabold text-3xl md:text-4xl text-foreground" data-id="element-418">
              Preguntas frecuentes 💬
            </h2>
            <p className="mt-3 text-muted-foreground" data-id="element-419">
              Resolvemos las dudas más comunes antes de que empieces.
            </p>
          </div>
          <div className="mt-10 reveal" data-id="element-420">
            <Suspense fallback={<LazyFallback minH="16rem" data-id="element-422" />} data-id="element-421">
              <FAQAccordion items={faqs} data-id="element-423" />
            </Suspense>
          </div>
        </div>
      </section>

      {/* ========== BLOQUE 11.5: LO QUE NO SE TRABAJA EN LA INFANCIA ========== */}
      <section className="bg-gradient-night text-white relative overflow-hidden" data-id="element-424">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-gold/10 rounded-full blur-3xl pointer-events-none" aria-hidden="true" data-id="element-425" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-coral/10 rounded-full blur-3xl pointer-events-none" aria-hidden="true" data-id="element-426" />
        <div className="container mx-auto px-4 md:px-6 py-14 md:py-20 relative" data-id="element-427">
          <div className="max-w-3xl mx-auto text-center reveal" data-id="element-428">
            <p className="inline-flex items-center gap-2 text-gold text-xs sm:text-sm font-bold uppercase tracking-widest mb-5 bg-white/5 border border-gold/30 px-3 py-1.5 rounded-full" data-id="element-429">
              ⏳ La verdad que pocos te dicen
            </p>
            <h2 className="font-display font-extrabold text-3xl md:text-5xl leading-tight text-white" data-id="element-430">
              Lo que no se trabaja en la infancia…{' '}
              <span className="text-gold" data-id="element-431">se paga en la adultez.</span>
            </h2>
            <p className="mt-6 text-base sm:text-lg text-white/80 leading-relaxed max-w-2xl mx-auto" data-id="element-432">
              La inseguridad, el miedo a equivocarse, el «no soy suficiente» que
              hoy ves en tu hijo no desaparecen solos. Se vuelven el diálogo
              interno con el que vivirá toda su vida adulta.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-5 max-w-4xl mx-auto" data-id="element-433">
            <div className="reveal bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-7 hover:-translate-y-1 transition-transform duration-300" style={{
            transitionDelay: '0ms'
          }} data-id="element-434">
              <div className="w-12 h-12 rounded-full bg-coral/20 border border-coral/40 flex items-center justify-center text-2xl mb-4" data-id="element-435">
                🗣️
              </div>
              <h3 className="font-display font-extrabold text-xl sm:text-2xl text-white leading-snug" data-id="element-436">
                Su voz de hoy será su diálogo interno mañana
              </h3>
              <p className="mt-3 text-white/75 leading-relaxed" data-id="element-437">
                Cada vez que un niño dice «no puedo» o «soy un torpe», está
                grabando una creencia. Cambiar esas palabras hoy es darle un
                adulto seguro, capaz y amado por sí mismo.
              </p>
            </div>

            <div className="reveal bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-7 hover:-translate-y-1 transition-transform duration-300" style={{
            transitionDelay: '80ms'
          }} data-id="element-438">
              <div className="w-12 h-12 rounded-full bg-gold/20 border border-gold/40 flex items-center justify-center text-2xl mb-4" data-id="element-439">
                🌱
              </div>
              <h3 className="font-display font-extrabold text-xl sm:text-2xl text-white leading-snug" data-id="element-440">
                Pequeños hábitos, grandes adultos
              </h3>
              <p className="mt-3 text-white/75 leading-relaxed" data-id="element-441">
                10 minutos de cuento antes de dormir. Una canción mindset en el
                auto. Un ejercicio del cuadernillo el fin de semana. Es así, en
                gestos chiquitos, que se construye una autoestima
                inquebrantable.
              </p>
            </div>
          </div>

          <div className="reveal mt-12 max-w-2xl mx-auto text-center" data-id="element-442">
            <p className="font-display font-extrabold text-xl md:text-2xl text-white leading-snug" data-id="element-443">
              Sembrá hoy la confianza que lo acompañará{' '}
              <span className="text-gold" data-id="element-444">toda la vida.</span>
            </p>
            <div className="mt-7" data-id="element-445">
              <a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" className={ctaPrimary} onClick={() => handleInitiateCheckout('infancia-adultez-cta')} data-id="element-446">
                Quiero sembrar eso en mi hijo/a
              </a>
              <p className="mt-3 text-xs text-white/60" data-id="element-447">
                ⚡Acceso inmediato post-compra⚡
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== BLOQUE 12: CIERRE EMOCIONAL ========== */}
      <section className="bg-gradient-warm-gold" data-id="element-448">
        <div className="container mx-auto px-4 md:px-6 py-10 md:py-16 grid md:grid-cols-2 gap-12 items-center" data-id="element-449">
          <div className="reveal" data-id="element-450">
            <div className="relative" data-id="element-451">
              <div className="absolute -inset-4 bg-coral/15 rounded-[2rem] blur-2xl" aria-hidden="true" data-id="element-452" />
              <img src={imgCierreEmocional} alt="Niño feliz corriendo por la vereda con sus padres detrás, brillo de superpoder en el pecho" loading="lazy" decoding="async" width={800} height={1200} className="relative rounded-[2rem] shadow-soft w-full h-auto object-cover max-h-[560px] mx-auto" data-id="element-453" />
            </div>
          </div>
          <div className="reveal" data-id="element-454">
            <h2 className="font-display font-extrabold text-3xl md:text-4xl leading-tight text-[#F87171]" data-id="element-455">
              Imaginá a tu hijo/a...
            </h2>
            <p className="mt-5 text-lg text-foreground/85 leading-relaxed" data-id="element-456">
              Levantándose con la mano en el pecho, recordando que{' '}
              <span className="font-bold" data-id="element-457">tal como es, vale un montón</span>.
              Resolviendo una frustración con tres respiraciones profundas.
              Diciendo{' '}
              <em data-id="element-458">«aún no me sale, pero lo voy a intentar otra vez»</em> en
              lugar de rendirse.
            </p>
            <p className="mt-4 text-lg text-foreground/85 leading-relaxed" data-id="element-459">
              Esa transformación no se construye con un sermón. Se construye{' '}
              <span className="font-bold text-[#F87171]" data-id="element-460">
                cuento a cuento, latido a latido.
              </span>
            </p>
            <div className="mt-8" data-id="element-461">
              <a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" className={ctaPrimary} onClick={() => handleInitiateCheckout('cierre-empezar-transformacion')} data-id="element-462">
                Activar sus superpoderes
              </a>

              <PaymentTrust className="mt-4" data-id="element-463" />
            </div>
          </div>
        </div>
      </section>

      {/* ========== BLOQUE 13: FOOTER (minimal) ========== */}
      <footer className="bg-foreground text-white/80" data-id="element-464">
        <div className="container mx-auto px-4 md:px-6 py-10 flex flex-col items-center text-center gap-4" data-id="element-465">
          <a href="https://www.instagram.com/sonriealmitamia/" target="_blank" rel="noopener noreferrer" aria-label="Seguinos en Instagram @sonriealmitamia" className="inline-flex items-center gap-2 text-white hover:text-gold transition-colors font-display font-bold" data-id="element-466">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" aria-hidden="true" data-id="element-467">
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5" data-id="element-468" />
              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" data-id="element-469" />
              <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" data-id="element-470" />
            </svg>
            @sonriealmitamia
          </a>
          <p className="text-xs text-white/50 max-w-md leading-relaxed" data-id="element-471">
            © Los superpoderes que hay en mí es un producto de @sonriealmitamía
            2026. Todos los derechos reservados®
          </p>
        </div>
      </footer>
    </div>;
};
export default Index;